import { currentRange } from "./richTextCommands";
import { offsetOf, pointAt } from "./richTextSelection";
import { MergeAttributeClass } from "./sanitiseEmailHtml";

/** The text as it stood, with the selection as character positions so it survives being rebuilt. */
export interface Snapshot {
  html: string;
  start: number;
  end: number;
}

const MaxStates = 100;
const TypingPause = 1000;

const indexOf = (node: Node) => Array.prototype.indexOf.call(node.parentNode?.childNodes ?? [], node) as number;

export function takeSnapshot(root: HTMLElement): Snapshot {
  const range = currentRange(root);
  const end = (root.textContent ?? "").length;

  return {
    html: root.innerHTML,
    start: range ? offsetOf(root, range.startContainer, range.startOffset) : end,
    end: range ? offsetOf(root, range.endContainer, range.endOffset) : end,
  };
}

/** Writes the snapshot back and selects what was selected. */
export function applySnapshot(root: HTMLElement, snapshot: Snapshot) {
  const range = document.createRange();

  root.innerHTML = snapshot.html;

  [snapshot.start, snapshot.end].forEach((position, index) => {
    const [node, offset] = pointAt(root, position);
    const chip = node.parentElement?.closest(`span.${MergeAttributeClass}`);

    // A merge attribute is one piece, so the caret goes beside it, not inside.
    const [target, at] = chip && root.contains(chip) ? [chip.parentNode as Node, indexOf(chip) + 1] : [node, offset];

    if (index === 0) {
      range.setStart(target, at);
    } else {
      range.setEnd(target, at);
    }
  });

  window.getSelection()?.removeAllRanges();
  window.getSelection()?.addRange(range);
}

/** The editor's own undo and redo, since its edits are made on the page and the browser does not record them. */
export class EditHistory {
  private states: Snapshot[] = [];

  private index = -1;

  private typedAt = 0;

  reset(snapshot: Snapshot) {
    this.states = [snapshot];
    this.index = 0;
    this.typedAt = 0;
  }

  /** Records a change. Typing in a run joins one step; anything else is a step of its own. */
  record(snapshot: Snapshot, typing: boolean, now = Date.now()) {
    if (this.index < 0) {
      this.reset(snapshot);
      return;
    }

    if (this.states[this.index].html === snapshot.html) {
      this.states[this.index] = snapshot;
      return;
    }

    this.states.length = this.index + 1;

    if (typing && this.typedAt && now - this.typedAt < TypingPause) {
      this.states[this.index] = snapshot;
    } else {
      this.states.push(snapshot);
      this.index += 1;
    }

    if (this.states.length > MaxStates) {
      this.states.shift();
      this.index -= 1;
    }

    this.typedAt = typing ? now : 0;
  }

  get canUndo() {
    return this.index > 0;
  }

  get canRedo() {
    return this.index >= 0 && this.index < this.states.length - 1;
  }

  undo() {
    if (!this.canUndo) {
      return null;
    }

    this.index -= 1;
    this.typedAt = 0;
    return this.states[this.index];
  }

  redo() {
    if (!this.canRedo) {
      return null;
    }

    this.index += 1;
    this.typedAt = 0;
    return this.states[this.index];
  }
}
